import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import logo from "../assets/logo/proplay-logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

function FloatingOrb({ className, duration = 10, delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{
        opacity: 1,
        y: [0, -14, 0],
        x: [0, 10, 0],
        scale: [1, 1.03, 1],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        repeatType: "mirror",
        ease: "easeInOut",
      }}
    />
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-left">
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-2 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

export default function HeroSection() {
  const reduceMotion = useReducedMotion();

  return (
    <section id="top" className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-dark to-black" />

        {/* Motion objects */}
        <div className="absolute inset-0">
          <FloatingOrb
            className="absolute -top-48 left-1/2 h-[640px] w-[640px] -translate-x-1/2 rounded-full bg-brand-red/18 blur-3xl"
            duration={reduceMotion ? 0 : 12}
          />
          <FloatingOrb
            className="absolute -bottom-56 right-[-160px] h-[720px] w-[720px] rounded-full bg-white/6 blur-3xl"
            duration={reduceMotion ? 0 : 14}
            delay={0.2}
          />
          <FloatingOrb
            className="absolute top-32 left-[-140px] h-[420px] w-[420px] rounded-full bg-brand-red/10 blur-3xl"
            duration={reduceMotion ? 0 : 16}
            delay={0.4}
          />
        </div>

        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-12 sm:pt-16 sm:pb-20">
        <div className="mx-auto max-w-3xl text-center">
          {/* Big Logo */}
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="flex justify-center"
          >
            <img
  src={logo}
  alt="ProPlay Studios"
  className="h-36 w-auto object-contain sm:h-44 md:h-52 lg:h-64 xl:h-72 drop-shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
  draggable="false"
/>

          </motion.div>

          <motion.h1
            className="mt-6 text-4xl font-extrabold tracking-tight text-white sm:text-5xl"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.75, ease: "easeOut", delay: 0.05 }}
          >
            We build <span className="text-brand-red">high-impact</span> games & interactive experiences.
          </motion.h1>

          <motion.p
            className="mt-4 text-base leading-relaxed text-white/70 sm:text-lg"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.75, ease: "easeOut", delay: 0.1 }}
          >
            A modern game studio focused on polished gameplay, rapid iteration,
            and cinematic presentation.
          </motion.p>

          <motion.div
            className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.75, ease: "easeOut", delay: 0.15 }}
          >
            <a
              href="#contact"
              className="w-full rounded-xl bg-brand-red px-6 py-3 text-sm font-semibold text-white shadow-soft transition hover:opacity-90 sm:w-auto"
            >
              Work with us
            </a>
            <a
              href="#studio"
              className="w-full rounded-xl border border-white/15 px-6 py-3 text-sm font-semibold text-white transition hover:border-white/30 sm:w-auto"
            >
              How we build
            </a>
          </motion.div>

          <motion.div
            className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.75, ease: "easeOut", delay: 0.2 }}
          >
            <StatCard label="Focus" value="Gameplay + Polish" />
            <StatCard label="Speed" value="Rapid Iteration" />
            <StatCard label="Style" value="Bold & Clean" />
          </motion.div>

          {/* Scroll cue */}
          <motion.div
            className="mt-10 flex justify-center"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.25 }}
          >
            <motion.a
              href="#studio"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/60 hover:text-white"
              animate={reduceMotion ? {} : { y: [0, 6, 0] }}
              transition={reduceMotion ? {} : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              Scroll <span className="text-brand-red">↓</span>
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
